import type Luminary from '@lumen5/luminary';
import type { TimeAwareMediaAsset } from '@lumen5/luminary';

import { BackgroundVideoDurationSeconds } from 'js/src/client';
import { checkpointVideo } from 'js/src/data/video-data/video-data-actions';
import uuid from 'js/src/libs/uuid';
import {
    createLuminaryOperation,
    type LuminaryOperationContext,
} from 'js/src/luminary/luminary-hooks';
import { applyUserSceneDurationOperation } from 'js/src/luminary/luminary-operations/core';
import type { MorphChainMember } from 'js/src/luminary/morph-utils';
import {
    getMorphChainHeadAsset,
    getOrderedMorphChain,
    isChainTrimmable,
} from 'js/src/luminary/morph-utils';
import { saveRawLuminary } from 'js/src/luminary/utils';

export const MORPH_CHAIN_MIN_SCENE_DURATION_MS = BackgroundVideoDurationSeconds.MIN * 1000;

function absorbFromEdge(
    durations: number[],
    index: number,
    step: number,
    amount: number,
    floor: number,
): boolean {
    let remaining = amount;
    let current = index;
    while (remaining !== 0) {
        if (current < 0 || current >= durations.length) {
            return false;
        }
        const proposed = durations[current] + remaining;
        if (proposed >= floor) {
            durations[current] = proposed;
            return true;
        }
        remaining = proposed - floor;
        durations[current] = floor;
        current += step;
    }
    return true;
}

function allocateChainDurations(
    durations: readonly number[],
    startDelta: number,
    endDelta: number,
    floor: number = MORPH_CHAIN_MIN_SCENE_DURATION_MS,
): number[] | null {
    const allocated = [...durations];
    const headAbsorbs = -startDelta;
    const tailAbsorbs = endDelta;

    const absorbHead = () => absorbFromEdge(allocated, 0, 1, headAbsorbs, floor);
    const absorbTail = () =>
        absorbFromEdge(allocated, allocated.length - 1, -1, tailAbsorbs, floor);

    const succeeded =
        headAbsorbs > tailAbsorbs ? absorbHead() && absorbTail() : absorbTail() && absorbHead();
    return succeeded ? allocated : null;
}

function setPlaybackWindow(asset: TimeAwareMediaAsset, startTime: number, endTime: number) {
    if (startTime < asset.startTime) {
        asset.startTime = startTime;
    }
    if (endTime > (asset.endTime as number)) {
        asset.endTime = endTime;
    }
    if (startTime > asset.startTime) {
        asset.startTime = startTime;
    }
    if (endTime < (asset.endTime as number)) {
        asset.endTime = endTime;
    }
}

function getChainTotalDuration(chain: readonly MorphChainMember[]): number {
    return chain.reduce((total, { duration }) => total + duration, 0);
}

export const applyUserMorphChainTrimOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call }: LuminaryOperationContext<Luminary>,
        sceneId: string,
        assetId: string,
        startTime: number,
        stopTime: number,
        systemTriggered = false,
        parentActionGroup = uuid(),
    ): boolean => {
        const chain = getOrderedMorphChain(luminary, sceneId, assetId);
        if (!isChainTrimmable(luminary, chain)) {
            return false;
        }

        const head = getMorphChainHeadAsset(luminary, chain);
        if (!head || head.sourceDuration === null) {
            return false;
        }

        const shownStartTime = head.startTime;
        const shownStopTime = Math.min(
            head.endTime as number,
            shownStartTime + getChainTotalDuration(chain),
        );

        const newStartTime = Math.round(Math.max(startTime, 0));
        const newStopTime = Math.round(Math.min(stopTime, head.sourceDuration));
        if (newStartTime === shownStartTime && newStopTime === shownStopTime) {
            return false;
        }

        const durations = allocateChainDurations(
            chain.map(({ duration }) => duration),
            newStartTime - shownStartTime,
            newStopTime - shownStopTime,
        );
        if (!durations) {
            return false;
        }

        luminary.batchRun((safeLuminary) => {
            chain.forEach((member) => {
                const asset = safeLuminary
                    .getSceneById(member.sceneId)
                    ?.getAssetById(member.assetId) as TimeAwareMediaAsset | undefined;
                if (asset) {
                    setPlaybackWindow(asset, newStartTime, newStopTime);
                }
            });
            chain.forEach((member, index) => {
                call(
                    applyUserSceneDurationOperation,
                    member.sceneId,
                    durations[index],
                    systemTriggered,
                    parentActionGroup,
                    { luminary: safeLuminary },
                );
            });
        });

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );
        return true;
    },
);
